import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '@/LanguageContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

// Proje detay görünüm ayarları — varsayılanlar (admin panel "Proje Görünümü" tabı ile düzenlenir)
export const DEFAULT_DETAIL = {
  video_orientation: 'horizontal', // 'horizontal' | 'vertical'
  video_width: 100,                // yatay videoda genişlik (%)
  video_height: 80,                // dikey videoda maksimum yükseklik (vh)
  show_video_title: true,          // video üzerine başlık
  video_title_size: 5,             // başlık boyutu (rem)
  video_glow: true,                // neon glow çerçeve
  images: [],                      // [{ url, caption, size }] — size = genişlik %
  image_gap: 80,                   // fotoğraflar arası dikey boşluk (px)
  image_alternate: true,           // sağ/sol dönüşümlü
};

// ─── Lightbox ─────────────────────────────────────────────────────────────────
const Lightbox = ({ images, index, onClose }) => {
  const { t } = useLanguage();
  const [current, setCurrent] = useState(index);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setCurrent(c => Math.min(c + 1, images.length - 1));
      if (e.key === 'ArrowLeft')  setCurrent(c => Math.max(c - 1, 0));
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [images.length, onClose]);

  return (
    <motion.div
      className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <button onClick={onClose}
        className="absolute top-6 right-6 font-mono text-xs text-white/40 hover:text-white uppercase tracking-widest">
        {t('projectDetail', 'close', '[ Kapat ]')}
      </button>
      <div className="relative max-w-5xl w-full px-16" onClick={e => e.stopPropagation()}>
        <img src={images[current]} alt="" className="w-full max-h-[80vh] object-contain" />
        {images.length > 1 && (
          <div className="flex items-center justify-center gap-6 mt-4">
            <button onClick={() => setCurrent(c => Math.max(c - 1, 0))}
              disabled={current === 0}
              className="font-mono text-xs text-white/30 hover:text-white disabled:opacity-20 uppercase tracking-widest">
              {t('projectDetail', 'prev', '← Önceki')}
            </button>
            <span className="font-mono text-[10px] text-white/20">{current + 1} / {images.length}</span>
            <button onClick={() => setCurrent(c => Math.min(c + 1, images.length - 1))}
              disabled={current === images.length - 1}
              className="font-mono text-xs text-white/30 hover:text-white disabled:opacity-20 uppercase tracking-widest">
              {t('projectDetail', 'next', 'Sonraki →')}
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────
export const ProjectDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null); // index or null

  useEffect(() => {
    fetch(`${API}/projects`)
      .then(r => r.json())
      .then(data => {
        const found = Array.isArray(data) ? data.find(p => p.id === id) : null;
        setProject(found || null);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-px h-10 bg-white/20" style={{ animation: 'pulse 1.5s ease-in-out infinite' }} />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center gap-6">
        <p className="font-mono text-xs text-white/30 uppercase tracking-widest">{t('projectDetail', 'notFound', 'Proje bulunamadı')}</p>
       <Link
  to="/projects"
  className="absolute top-8 left-8 font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 hover:text-white transition-colors flex items-center gap-2"
>
  {t('projectDetail', 'back', '← Projelere Dön')}
</Link>
      </div>
    );
  }

  const tags = Array.isArray(project.tags)
    ? project.tags
    : typeof project.tags === 'string'
      ? project.tags.split(',').map(t => t.trim()).filter(Boolean)
      : [];

  const allImages = [
    project.image,
    ...(Array.isArray(project.images) ? project.images : []),
  ].filter(Boolean);

  // ── Detay görünüm ayarları ──
  const detail = { ...DEFAULT_DETAIL, ...(project.detail || {}) };
  const hasVideo = !!project.video_url;
  const isVertical = detail.video_orientation === 'vertical';
  const detailImages = Array.isArray(detail.images)
    ? detail.images.filter(it => it && it.url)
    : [];
  // Lightbox / galeri kaynağı: özel görseller varsa onları, yoksa eski diziyi kullan
  const galleryImages = detailImages.length ? detailImages.map(it => it.url) : allImages;

  // Glow çerçeve stili (yatay/dikey ortak)
  const videoFrameStyle = {
    borderRadius: '1.5rem 0.4rem 1.5rem 0.4rem',
    border: detail.video_glow ? '1px solid rgba(226,255,0,0.15)' : '1px solid rgba(255,255,255,0.08)',
    boxShadow: detail.video_glow
      ? '0 0 40px rgba(226,255,0,0.06), 0 0 80px rgba(0,0,0,0.6)'
      : '0 0 60px rgba(0,0,0,0.6)',
    background: '#080808',
  };

  // Video üzerine büyük başlık
  const VideoTitle = () => (
    detail.show_video_title ? (
      <div className="absolute left-5 bottom-5 right-5 pointer-events-none">
        <h2 className="font-bebas leading-none tracking-tighter uppercase"
            style={{ fontSize: `clamp(2rem, ${detail.video_title_size}vw, ${detail.video_title_size}rem)`,
                     textShadow: '0 2px 30px rgba(0,0,0,0.8)' }}>
          {project.title}
        </h2>
      </div>
    ) : null
  );

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&display=swap');
        .font-bebas { font-family: 'Bebas Neue', sans-serif; }
        :root { --accent: #E2FF00; }
      `}</style>

      <div className="min-h-screen bg-[#050505] text-white">

        {/* ── Hero ── */}
        <div className="relative w-full overflow-hidden" style={{ height: '70vh' }}>
          <img
            src={project.image}
            alt={project.title}
            className="w-full h-full object-cover"
            style={{ filter: 'brightness(0.4)' }}
          />
          {/* Gradient */}
          <div className="absolute inset-0"
               style={{ background: 'linear-gradient(to top, #050505 15%, transparent 60%)' }} />



          {/* Title block */}
          <motion.div
            className="absolute bottom-12 left-8 md:left-16 right-8 md:right-16"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.23, 1, 0.32, 1] }}
          >
            <div className="flex items-center gap-3 mb-3"
                 style={{ color: 'var(--accent)', fontSize: '10px', letterSpacing: '0.4em' }}>
              <span className="w-6 h-px" style={{ background: 'var(--accent)' }} />
              <span className="font-mono uppercase">{tags[0] || 'Proje'}</span>
            </div>
            <h1 className="font-bebas leading-none tracking-tighter uppercase"
                style={{ fontSize: 'clamp(3.5rem, 9vw, 8rem)' }}>
              {project.title}
            </h1>
          </motion.div>
        </div>

        {/* ── Meta + Description ── */}
        <div className={`px-8 md:px-16 lg:px-24 py-16 grid grid-cols-1 gap-12 ${hasVideo ? '' : 'md:grid-cols-3'}`}>

          {/* Sol: Meta */}
          <motion.div
            className="space-y-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            {project.author && (
              <div>
                <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/30 mb-1">{t('projectDetail', 'author', 'Ekip / Yazar')}</p>
                <p className="font-mono text-sm text-white/70">{project.author}</p>
              </div>
            )}
            {project.client && (
              <div>
                <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/30 mb-1">{t('projectDetail', 'client', 'Müşteri')}</p>
                <p className="font-mono text-sm text-white/70">{project.client}</p>
              </div>
            )}
            {project.year && (
              <div>
                <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/30 mb-1">{t('projectDetail', 'year', 'Yıl')}</p>
                <p className="font-mono text-sm text-white/70">{project.year}</p>
              </div>
            )}
            {tags.length > 0 && (
              <div>
                <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/30 mb-2">{t('projectDetail', 'categories', 'Kategoriler')}</p>
                <div className="flex flex-wrap gap-2">
                  {tags.map(tag => (
                    <span key={tag}
                      className="font-mono text-[9px] uppercase tracking-widest px-2 py-1 border border-white/10 text-white/50">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {project.link && (
              <a href={project.link} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.3em] transition-colors"
                style={{ color: 'var(--accent)' }}>
                {t('projectDetail', 'view_live', 'Projeyi Görüntüle →')}
              </a>
            )}
          </motion.div>

          {/* Sağ: Açıklama — video yoksa burada; video varsa video bölümünde gösterilir */}
          {!hasVideo && (
            <motion.div
              className="md:col-span-2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
            >
              {project.description ? (
                <p className="font-mono text-sm text-white/60 leading-relaxed whitespace-pre-line">
                  {project.description}
                </p>
              ) : (
                <p className="font-mono text-xs text-white/20 uppercase tracking-widest">
                  {t('projectDetail', 'no_desc', 'Açıklama eklenmemiş')}
                </p>
              )}
            </motion.div>
          )}
        </div>

        {/* —— Video —— */}
        {hasVideo && (
          <div className="px-8 md:px-16 lg:px-24 pb-16">
            {isVertical ? (
              /* DİKEY: açıklama solda, video sağda */
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
                <motion.div
                  className="order-2 md:order-1"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7 }}
                >
                  {project.description && (
                    <p className="font-mono text-sm text-white/60 leading-relaxed whitespace-pre-line">
                      {project.description}
                    </p>
                  )}
                </motion.div>
                <motion.div
                  className="order-1 md:order-2 relative overflow-hidden mx-auto"
                  style={{ ...videoFrameStyle, maxHeight: `${detail.video_height}vh` }}
                  initial={{ opacity: 0, y: 20, scale: 0.98 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, ease: [0.23, 1, 0.32, 1] }}
                >
                  <video
                    src={project.video_url}
                    autoPlay muted loop playsInline controls
                    className="w-full object-contain"
                    style={{ borderRadius: 'inherit', display: 'block', maxHeight: `${detail.video_height}vh` }}
                  />
                  <VideoTitle />
                </motion.div>
              </div>
            ) : (
              /* YATAY: tam genişlik video + altında açıklama */
              <div>
                <motion.div
                  className="relative overflow-hidden mx-auto"
                  style={{ ...videoFrameStyle, width: `${detail.video_width}%` }}
                  initial={{ opacity: 0, y: 20, scale: 0.98 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, ease: [0.23, 1, 0.32, 1] }}
                  whileHover={detail.video_glow ? { boxShadow: '0 0 60px rgba(226,255,0,0.12), 0 0 120px rgba(0,0,0,0.8)' } : undefined}
                >
                  <video
                    src={project.video_url}
                    autoPlay muted loop playsInline controls
                    className="w-full object-cover"
                    style={{ borderRadius: 'inherit', display: 'block' }}
                  />
                  <VideoTitle />
                </motion.div>
                {project.description && (
                  <motion.p
                    className="font-mono text-sm text-white/60 leading-relaxed whitespace-pre-line mt-8 mx-auto"
                    style={{ width: `${detail.video_width}%` }}
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.1 }}
                  >
                    {project.description}
                  </motion.p>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Görseller ── */}
        {detailImages.length > 0 ? (
          /* Dönüşümlü düzen: 1. foto sağ / açıklama sol, 2. foto sol / açıklama sağ ... */
          <div className="px-8 md:px-16 lg:px-24 pb-24" style={{ display: 'flex', flexDirection: 'column', gap: `${detail.image_gap}px` }}>
            {detailImages.map((it, i) => {
              const imgRight = detail.image_alternate ? i % 2 === 0 : true;
              const size = Math.min(Math.max(Number(it.size) || 100, 20), 100);
              return (
                <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
                  {/* Görsel */}
                  <motion.div
                    className={`overflow-hidden cursor-pointer bg-[#111] ${imgRight ? 'md:order-2' : 'md:order-1'}`}
                    style={{ border: '1px solid rgba(255,255,255,0.06)', width: `${size}%`,
                             marginLeft: imgRight ? 'auto' : undefined, marginRight: imgRight ? undefined : 'auto' }}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-60px' }}
                    transition={{ duration: 0.7, ease: [0.23, 1, 0.32, 1] }}
                    onClick={() => setLightbox(i)}
                    whileHover={{ scale: 1.01 }}
                  >
                    <img src={it.url} alt={it.caption || ''} className="w-full h-auto object-cover transition-all duration-500 hover:brightness-75" />
                  </motion.div>
                  {/* Açıklama */}
                  <motion.div
                    className={imgRight ? 'md:order-1' : 'md:order-2'}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-60px' }}
                    transition={{ duration: 0.7, delay: 0.1 }}
                  >
                    {it.caption && (
                      <p className="font-mono text-sm text-white/60 leading-relaxed whitespace-pre-line">{it.caption}</p>
                    )}
                  </motion.div>
                </div>
              );
            })}
          </div>
        ) : allImages.length > 1 && (
          <div className="px-8 md:px-16 lg:px-24 pb-24">
            <div className="flex items-center gap-3 mb-8"
                 style={{ color: 'rgba(255,255,255,0.2)', fontSize: '10px', letterSpacing: '0.4em' }}>
              <span className="w-6 h-px bg-white/20" />
              <span className="font-mono uppercase">{t('projectDetail', 'images_label', 'Görseller')}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {allImages.map((img, i) => (
                <motion.div
                  key={i}
                  className="overflow-hidden cursor-pointer aspect-video bg-[#111]"
                  style={{ border: '1px solid rgba(255,255,255,0.06)' }}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  onClick={() => setLightbox(i)}
                  whileHover={{ scale: 1.02 }}
                >
                  <img src={img} alt="" className="w-full h-full object-cover transition-all duration-500 hover:brightness-75" />
                </motion.div>
              ))}
            </div>
          </div>
        )}


        {/* ── İçerik Blokları ── */}
        {Array.isArray(project.blocks) && project.blocks.length > 0 && (
          <div className="px-8 md:px-16 lg:px-24 pb-16 space-y-20">
            {project.blocks.sort((a,b) => a.order - b.order).map((block, i) => (
              <motion.div key={block.id || i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.8, ease: [0.23, 1, 0.32, 1] }}
              >
                {/* Sadece Metin */}
                {block.type === 'text' && (
                  <div className="max-w-2xl">
                    {block.title && (
                      <h3 className="font-bebas text-3xl md:text-5xl tracking-tight uppercase mb-4" style={{ color: 'var(--accent)' }}>
                        {block.title}
                      </h3>
                    )}
                    {block.text && (
                      <p className="font-mono text-sm text-white/60 leading-relaxed whitespace-pre-line">{block.text}</p>
                    )}
                  </div>
                )}

                {/* Sadece Görsel */}
                {block.type === 'image' && block.media_url && (
                  <div className="w-full overflow-hidden border border-white/5">
                    <img src={block.media_url} alt="" className="w-full object-cover" />
                  </div>
                )}

                {/* Sadece Video */}
                {block.type === 'video' && block.media_url && (
                  <motion.div
                    className="relative w-full overflow-hidden"
                    style={{
                      borderRadius: '1.5rem 0.4rem 1.5rem 0.4rem',
                      border: '1px solid rgba(226,255,0,0.12)',
                      boxShadow: '0 0 30px rgba(226,255,0,0.05), 0 0 60px rgba(0,0,0,0.5)',
                      background: '#080808',
                    }}
                    whileHover={{ boxShadow: '0 0 50px rgba(226,255,0,0.1), 0 0 100px rgba(0,0,0,0.7)' }}
                  >
                    <video
                      src={block.media_url}
                      controls
                      playsInline
                      className="w-full object-cover"
                      style={{ borderRadius: 'inherit', display: 'block' }}
                    />
                  </motion.div>
                )}

                {/* Medya + Metin */}
                {block.type === 'media_text' && (
                  <div className={`grid grid-cols-1 md:grid-cols-2 gap-10 items-center ${block.layout === 'right' ? 'md:[direction:rtl]' : ''}`}>
                    {/* Medya */}
                      {block.media_url && (
                        <div className="w-full overflow-hidden md:[direction:ltr]">
                          {block.media_url.match(/\.(mp4|webm|ogg)$/i) ? (
                            <motion.div
                              className="relative overflow-hidden"
                              style={{
                                borderRadius: '1.5rem 0.4rem 1.5rem 0.4rem',
                                border: '1px solid rgba(226,255,0,0.12)',
                                boxShadow: '0 0 30px rgba(226,255,0,0.05)',
                                background: '#080808',
                              }}
                              whileHover={{ boxShadow: '0 0 50px rgba(226,255,0,0.1)' }}
                            >
                              <video
                                src={block.media_url}
                                controls
                                playsInline
                                className="w-full object-cover"
                                style={{ borderRadius: 'inherit', display: 'block' }}
                              />
                            </motion.div>
                          ) : (
                            <img src={block.media_url} alt="" className="w-full object-cover" />
                          )}
                        </div>
                      )}
                    {/* Metin */}
                    <div className="md:[direction:ltr]">
                      {block.title && (
                        <h3 className="font-bebas text-3xl md:text-5xl tracking-tight uppercase mb-4" style={{ color: 'var(--accent)' }}>
                          {block.title}
                        </h3>
                      )}
                      {block.text && (
                        <p className="font-mono text-sm text-white/60 leading-relaxed whitespace-pre-line">{block.text}</p>
                      )}
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}

        {/* ── Footer nav ── */}
        <div className="px-8 md:px-16 lg:px-24 pb-20 flex justify-between items-center border-t border-white/5 pt-10">
          <Link to="/projects"
            className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/30 hover:text-white transition-colors">
            {t('projectDetail', 'all_projects', '← Tüm Projeler')}
          </Link>
          <button
            onClick={() => { sessionStorage.setItem('scrollTo', '#contact'); navigate('/'); }}
            className="font-mono text-[10px] uppercase tracking-[0.3em] transition-colors hover:text-white"
            style={{ color: 'var(--accent)' }}>
            {t('projectDetail', 'contact', 'İletişime Geç →')}
          </button>
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox !== null && (
          <Lightbox images={galleryImages} index={lightbox} onClose={() => setLightbox(null)} />
        )}
      </AnimatePresence>
    </>
  );
};

export default ProjectDetailPage;